/* eslint-disable spire/export-styles */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import NavigationModeMenu, { NavigationModeMenuStyles } from "@insite/content-library/Components/NavigationModeMenu";
import baseTheme from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import getContrastColor from "@insite/mobius/utilities/getContrastColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import * as cssLinter from "css";
import React, { FC } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.customCSS]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    enableWarehousePickup: getSettingsCollection(state).accountSettings.enableWarehousePickup,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const Navigation = styled.div`
    color: ${getContrastColor("common.accent")};
    font-size: 12px;
    background-color: #343434;
    padding: 0;
    height: auto;
    text-transform: uppercase;
`;

const NavItem = styled.span<InjectableCss>`
    display: inline-flex;

    > span,
    > a {
        margin-left: 10px;
    }
    ${injectCss}
`;

export interface SecondaryNavigationStyles {
    wrapper?: InjectableCss;
    innerWrapper?: InjectableCss;
    navigationModeMenu?: NavigationModeMenuStyles;
    languageLink?: InjectableCss;
    warehousePickupLink?: InjectableCss;
    signInLink?: InjectableCss;
    leftContent?: InjectableCss;
    rightContent?: InjectableCss;
}

export const secondaryNavigationStyles: SecondaryNavigationStyles = {
    innerWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin: 0 auto;
            ${({ theme }) => {
                const { maxWidths } = theme.breakpoints || baseTheme.breakpoints;
                return breakpointMediaQueries(theme, [
                    css`
                        max-width: ${maxWidths[0]}px;
                        flex-wrap: wrap;
                        padding: 5px 15px;
                    `,
                    css`
                        max-width: ${maxWidths[1]}px;
                        flex-wrap: wrap;
                        padding: 5px 15px;
                    `,
                    css`
                        max-width: ${maxWidths[2]}px;
                        flex-wrap: wrap;
                        padding: 5px 15px;
                    `,
                    css`
                        max-width: ${maxWidths[3]}px;
                        padding: 11px 40px;
                    `,
                    css`
                        max-width: ${maxWidths[4]}px;
                        padding: 11px 40px;
                    `,
                ]);
            }}
        `,
    },
    languageLink: {
        css: css`
            flex: 0 0 auto;
            margin-left: 30px;
        `,
    },
    warehousePickupLink: {
        css: css`
            flex: 0 1 auto;
            margin-left: 30px;
            min-width: 0;
        `,
    },
    signInLink: {
        css: css`
            flex: 0 0 auto;
            margin-left: 20px;
            align-items: center;
        `,
    },
    leftContent: {
        css: css`
            display: flex;
            align-items: center;
            span {
                color: white;
            }
            > span {
                &:after {
                    content: "";
                    width: 1px;
                    height: 12px;
                    background-color: #fff;
                    margin: 4px 0 0 20px;
                }
                &:last-child {
                    &:after {
                        content: none;
                    }
                }
                &:first-child {
                    margin-left: 0px;
                }
            }
        `,
    },
    rightContent: {
        css: css`
            display: flex;
            align-items: center;
            > span {
                &:after {
                    content: "";
                    width: 0.5px;
                    height: 12px;
                    background-color: #fff;
                    margin: 4px 0 0 20px;
                }
             
                &:last-child {
                    &:after {
                        content: "";
                        width: 0px;
                        margin: 0;
                    }
                }
            }
        `,
    },
};

const defaultCustomCss = `.wrapper {
}

.navigation-mode-menu {
}

.currency-menu {
}

.language-menu {
}

.ship-to-address-menu {
}

.sign-in-link {
}`;

const SecondaryNavigation: FC<Props> = ({ id, enableWarehousePickup, fields }) => {
    const styles = useMergeStyles("secondaryNavigation", secondaryNavigationStyles);

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <Hidden below="lg">
                <Navigation className="wrapper" {...styles.wrapper}>
                    <StyledWrapper {...styles.innerWrapper}>
                        <StyledWrapper {...styles.leftContent}>
                            <NavItem className="link-list" {...styles.signInLink}>
                                <Zone zoneName="ContactUs" contentId={id} />
                            </NavItem>
                            <NavItem className="link-list" {...styles.signInLink}>
                                <Zone zoneName="AboutUs" contentId={id} />
                            </NavItem>
                            <NavItem className="link-list" {...styles.signInLink}>
                                <Zone zoneName="AdditionalLink" contentId={id} />
                            </NavItem>
                        </StyledWrapper>
                        <StyledWrapper {...styles.rightContent}>
                            <NavItem className="link-list" {...styles.signInLink}>
                                <Zone zoneName="Support" contentId={id} />
                            </NavItem>
                            <NavItem className="link-list" {...styles.signInLink}>
                                <Zone zoneName="HowToBuy" contentId={id} />
                            </NavItem>
                            <NavItem className="link-list" {...styles.signInLink}>
                                <Zone zoneName="SignIn" contentId={id} />
                            </NavItem>
                            <NavItem className="link-list" {...styles.signInLink}>
                                <Zone zoneName="CrossReference" contentId={id} />
                            </NavItem>
                        </StyledWrapper>
                    </StyledWrapper>
                </Navigation>
            </Hidden>
        </StyledWrapper>
    );
};

const secondaryNavigation: WidgetModule = {
    component: connect(mapStateToProps)(SecondaryNavigation),
    definition: {
        group: "Common",
        icon: "link-simple",
        fieldDefinitions: [
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default secondaryNavigation;
